import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { serverEvents, debounce } from '../lib/events.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import type { ServerEvent } from '../lib/events.js';
import type { MemberDetail, MemberPointRow, HistoryRow, AccountRow } from '../lib/api.js';

type Mode = 'list' | 'detail' | 'create';
type Tab = 'info' | 'rewards' | 'history' | 'account';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'info', label: 'ℹ️ Info' },
  { id: 'rewards', label: '🎁 Membership Rewards' },
  { id: 'history', label: '🧾 Purchase History' },
  { id: 'account', label: '📒 Account' },
];

/** Everything kept per outlet so flipping back re-draws the exact screen
 *  (search text, results, open member, active tab). */
interface KeptMemberView {
  mode: Mode;
  search: string;
  items: Array<Record<string, unknown>>;
  error: string;
  notice: string;
  detail: MemberDetail | null;
  draft: Partial<MemberDetail> | null;
  dirty: boolean;
  tab: Tab;
  tabError: string;
  points: MemberPointRow[];
  history: HistoryRow[];
  account: AccountRow[];
  staleReloadId: string | null;
}

/**
 * Back-office member management panel.
 * List (search) → Edit/View → detail with tabs (Info | Membership Rewards |
 * Purchase History | Account), mirroring the desktop MembershipEditPanel.
 */
@customElement('member-manage')
export class MemberManage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }

    /* Detail tabs + list views: the header row stays fixed — only the data list scrolls. */
    .tab-body { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .tab-body > .grid { flex: 1; min-height: 0; overflow-y: auto; align-content: start; }
    .tab-body > .table-wrap { flex: 1; min-height: 0; overflow: auto; }
    .tab-body > .table-wrap thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }

    .list-tools { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; align-items: center; flex-shrink: 0; }
    .list-tools input { flex: 1; }

    .table-wrap { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 14px; overflow-x: auto; }
    th, td { white-space: nowrap; }
    th.num, td.num { text-align: right; }

    .fg { min-width: 0; }

    .rating { color: var(--color-success, #188038); font-weight: 600; }
    .debit { color: var(--color-error, #d93025); }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _mode: Mode = 'list';
  @state() private _search = '';
  @state() private _items: Array<Record<string, unknown>> = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _notice = '';

  @state() private _detail: MemberDetail | null = null;
  @state() private _draft: Partial<MemberDetail> | null = null;
  @state() private _dirty = false;
  @state() private _saving = false;

  @state() private _tab: Tab = 'info';
  @state() private _tabLoading = false;
  @state() private _tabError = '';
  @state() private _points: MemberPointRow[] = [];
  @state() private _history: HistoryRow[] = [];
  @state() private _account: AccountRow[] = [];

  // Live server events (auto-refresh)
  @state() private _staleReloadId: string | null = null;
  private _eventUnsubs: Array<() => void> = [];
  private _reloadDetailSoon = debounce((id: string) => void this._refreshDetailQuiet(id), 700);
  private _reloadListSoon = debounce(() => {
    if (this.isConnected && this._mode === 'list') void this._loadList();
  }, 700);

  /** Kept per-outlet view — flipping to another outlet saves this screen and
   *  re-draws it when the user flips back (see OutletViewCache). */
  private _view = new OutletViewCache<KeptMemberView>(this, 'member-manage', {
    capture: () => this._captureView(),
    restore: (v) => this._restoreView(v),
    reset: () => this._resetView(),
  });

  connectedCallback() {
    super.connectedCallback();
    this._eventUnsubs = [
      serverEvents.on('membership', this._onServerEvent),
      serverEvents.on('membershippoint', this._onServerEvent),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._reloadDetailSoon.cancel();
    this._reloadListSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
    // Keep the screen the user is leaving (also covers panel-type switches)
    // before this instance is discarded.
    this._view.dispose();
  }

  /**
   * The shell keeps this element mounted when switching to the same subtree of
   * another outlet and only re-binds `.storeCode` — property bindings never
   * fire attributeChangedCallback, so detect the change here. The outlet left
   * keeps its screen (search/results/open member/tab); a first visit loads fresh.
   */
  protected updated() {
    const result = this._view.switchTo(this.storeCode);
    if (result === 'unchanged') return;
    // Drop refreshes queued for the outlet the user just left.
    this._reloadDetailSoon.cancel();
    this._reloadListSoon.cancel();
    if (result === 'fresh') void this._loadList();
  }

  private _captureView(): KeptMemberView {
    return {
      mode: this._mode,
      search: this._search,
      items: this._items,
      error: this._error,
      notice: this._notice,
      detail: this._detail,
      draft: this._draft,
      dirty: this._dirty,
      tab: this._tab,
      tabError: this._tabError,
      points: this._points,
      history: this._history,
      account: this._account,
      staleReloadId: this._staleReloadId,
    };
  }

  private _restoreView(v: KeptMemberView) {
    this._mode = v.mode;
    this._search = v.search;
    this._items = v.items;
    this._error = v.error;
    this._notice = v.notice;
    this._detail = v.detail;
    this._draft = v.draft;
    this._dirty = v.dirty;
    this._tab = v.tab;
    this._tabError = v.tabError;
    this._points = v.points;
    this._history = v.history;
    this._account = v.account;
    this._staleReloadId = v.staleReloadId;
    // No request is in flight for a kept screen — keep spinners at rest.
    this._loading = false;
    this._saving = false;
    this._tabLoading = false;
  }

  private _resetView() {
    this._reset();
    this._search = '';
    this._loading = false;
    this._saving = false;
    this._tabLoading = false;
    this._tabError = '';
  }

  // ── List ──────────────────────────────────────────────────────────────

  private _reset() {
    this._mode = 'list';
    this._items = [];
    this._detail = null;
    this._draft = null;
    this._dirty = false;
    this._error = '';
    this._notice = '';
    this._tab = 'info';
    this._points = [];
    this._history = [];
    this._account = [];
    this._staleReloadId = null;
  }

  // ── Live server events (auto-refresh) ──────────────────────────────

  /** Mirrors the desktop behavior: refresh only when the event's record id
   *  matches the open detail; otherwise refresh the list. */
  private _onServerEvent = (ev: ServerEvent) => {
    if (ev.store !== this.storeCode) return;
    const id = ev.value;
    const detailMatch = this._mode === 'detail' && !!this._detail && String(this._detail.id) === id;

    if (detailMatch) {
      if (ev.code === 'deleted') {
        this._mode = 'list';
        this._detail = null;
        this._draft = null;
        this._dirty = false;
        this._staleReloadId = null;
        this._notice = 'This member was deleted on the server.';
        this._reloadListSoon();
        return;
      }
      if (this._dirty) {
        this._staleReloadId = id;
        return;
      }
      this._reloadDetailSoon(id);
      return;
    }

    if (this._mode === 'list') this._reloadListSoon();
  };

  /** Connection (re)established / ppServer reconnected — refresh visible view. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event !== 'resync') return;
    if (this._mode === 'detail' && this._detail && !this._dirty) {
      this._reloadDetailSoon(String(this._detail.id));
    } else if (this._mode === 'list') {
      this._reloadListSoon();
    }
  };

  private _reloadStaleRecord() {
    const id = this._staleReloadId;
    this._staleReloadId = null;
    if (id) void this._openDetail(id);
  }

  /** Quiet refresh of the open detail: keeps the active tab, re-fetches its data. */
  private async _refreshDetailQuiet(id: string) {
    if (!this.isConnected || this._dirty || this._saving) return;
    if (this._mode !== 'detail' || String(this._detail?.id ?? '') !== id) return;
    const epoch = this._view.epoch;
    try {
      const res = await api.staffMemberDetail(this.storeCode, id);
      if (!this._view.isCurrent(epoch)) return;
      if (!res.data || this._dirty || this._saving) return;
      const tab = this._tab;
      this._detail = res.data;
      this._draft = { ...res.data };
      this._points = [];
      this._history = [];
      this._account = [];
      this._tabError = '';
      if (tab !== 'info') this._switchTab(tab);
    } catch {
      // Transient — the next event / reconnect resync will retry.
    }
  }

  private async _loadList() {
    if (!this.storeCode) return;
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.staffMembers(this.storeCode, this._search);
      if (!this._view.isCurrent(epoch)) return;
      this._items = res.success ? (res.data as Array<Record<string, unknown>>) : [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load members';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private async _openDetail(id: string) {
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    this._notice = '';
    try {
      const res = await api.staffMemberDetail(this.storeCode, id);
      if (!this._view.isCurrent(epoch)) return;
      this._detail = res.data;
      this._draft = { ...res.data };
      this._dirty = false;
      this._mode = 'detail';
      this._tab = 'info';
      this._points = [];
      this._history = [];
      this._account = [];
      this._tabError = '';
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load member';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private _startCreate() {
    this._detail = null;
    this._draft = {
      code: '', name: '', ic: '', email: '', telNo: '', faxNo: '', contact: '',
      address: '', postcode: '', state: '', country: '', city: '', idType: '',
      notes: '', allergy: '', medicalHistory: '', longTermMedication: '', tin: '',
      discount: 0, creditTerm: 0, debitTerm: 0, creditLimit: 0,
      purposeOfSales: '', isTaxable: false, canSellPoison: true, enrollNumber: '',
    };
    this._dirty = false;
    this._mode = 'create';
    this._tab = 'info';
    this._error = '';
    this._notice = '';
  }

  private async _save() {
    if (!this._draft || !this.isAdmin) return;
    this._saving = true;
    this._error = '';
    this._notice = '';
    const epoch = this._view.epoch;
    try {
      if (this._mode === 'create') {
        const res = await api.staffMemberCreate(this.storeCode, this._draft);
        if (!this._view.isCurrent(epoch)) return;
        this._detail = res.data;
        this._draft = { ...res.data };
        this._mode = 'detail';
        this._notice = 'Member created.';
      } else if (this._detail) {
        const res = await api.staffMemberUpdate(this.storeCode, this._detail.id, this._draft);
        if (!this._view.isCurrent(epoch)) return;
        this._detail = res.data;
        this._draft = { ...res.data };
        this._notice = 'Saved.';
      }
      this._dirty = false;
      void this._loadList();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Save failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._saving = false;
    }
  }

  private async _delete() {
    if (!this._detail || !this.isAdmin) return;
    if (!window.confirm(`Delete member ${this._detail.name || this._detail.code}?`)) return;
    const epoch = this._view.epoch;
    this._error = '';
    try {
      await api.staffMemberDelete(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._reset();
      void this._loadList();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Delete failed';
    }
  }

  private _backToList() {
    if (this._dirty && !window.confirm('Discard unsaved changes?')) return;
    this._reset();
    void this._loadList();
  }

  // ── Tabs ──────────────────────────────────────────────────────────────

  private _switchTab(t: Tab) {
    this._tab = t;
    this._tabError = '';
    if (this._mode === 'create' || !this._detail) return;
    if (t === 'rewards' && this._points.length === 0) void this._loadPoints();
    if (t === 'history' && this._history.length === 0) void this._loadHistory();
    if (t === 'account' && this._account.length === 0) void this._loadAccount();
  }

  private async _loadPoints() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffMemberPoints(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._points = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load reward points';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadHistory() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffMemberHistory(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._history = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load purchase history';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  private async _loadAccount() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    this._tabLoading = true;
    try {
      const res = await api.staffMemberAccount(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._account = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._tabError = err instanceof Error ? err.message : 'Failed to load account statement';
    } finally {
      if (this._view.isCurrent(epoch)) this._tabLoading = false;
    }
  }

  // ── Field helpers ─────────────────────────────────────────────────────

  private _setField(key: string, value: unknown) {
    if (!this._draft) return;
    (this._draft as Record<string, unknown>)[key] = value;
    this._dirty = true;
  }

  private _text(label: string, key: string, value: unknown, type = 'text') {
    const v = value === undefined || value === null ? '' : String(value);
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${v || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <input type=${type} .value=${v}
          @input=${(e: InputEvent) => this._setField(key, (e.target as HTMLInputElement).value)} />
      </div>`;
  }

  private _number(label: string, key: string, value: unknown) {
    const num = typeof value === 'number' ? value : 0;
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${num || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <input type="number" step="any" .value=${String(num)}
          @input=${(e: InputEvent) =>
            this._setField(key, parseFloat((e.target as HTMLInputElement).value) || 0)} />
      </div>`;
  }

  private _bool(label: string, key: string, value: unknown) {
    const on = value === true;
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${on ? 'Yes' : 'No'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <div class="ro">
          <input type="checkbox" style="width:auto" .checked=${on}
            @change=${(e: Event) => this._setField(key, (e.target as HTMLInputElement).checked)} />
        </div>
      </div>`;
  }

  private _area(label: string, key: string, value: unknown, full = false) {
    const v = value === undefined || value === null ? '' : String(value);
    if (!this.isAdmin) {
      return html`<div class="fg ${full ? 'full' : ''}"><label>${label}</label><div class="ro">${v || '—'}</div></div>`;
    }
    return html`
      <div class="fg ${full ? 'full' : ''}">
        <label>${label}</label>
        <textarea .value=${v}
          @input=${(e: InputEvent) => this._setField(key, (e.target as HTMLTextAreaElement).value)}></textarea>
      </div>`;
  }

  private _dobField() {
    const sec = this._draft?.dob || 0;
    const v = sec > 0 ? new Date(sec * 1000).toISOString().slice(0, 10) : '';
    if (!this.isAdmin) {
      return html`<div class="fg"><label>Date of Birth</label><div class="ro">${v || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>Date of Birth</label>
        <input type="date" .value=${v}
          @input=${(e: InputEvent) => {
            const val = (e.target as HTMLInputElement).value;
            this._setField('dob', val ? Math.floor(new Date(val).getTime() / 1000) : 0);
          }} />
      </div>`;
  }

  private _fmtDate(sec?: number) {
    if (!sec) return '—';
    return new Date(sec * 1000).toISOString().slice(0, 10);
  }

  private _fmtMoney(n?: number) {
    return n === undefined || n === null ? '—' : `RM ${Number(n).toFixed(2)}`;
  }

  // ── Render ────────────────────────────────────────────────────────────

  render() {
    if (!this.storeCode) return html`<p style="padding:1rem">Select an outlet first.</p>`;
    return html`
      ${this._error ? html`<p class="err">${this._error}</p>` : ''}
      ${this._notice ? html`<p style="color:var(--color-success,#188038);font-size:0.8rem;margin:0.25rem 0">${this._notice}</p>` : ''}
      ${this._staleReloadId
        ? html`<p style="color:var(--color-warning,#b06000);font-size:0.8rem;margin:0.25rem 0;display:flex;align-items:center;gap:0.5rem">
            This member was updated on the server — your unsaved edits may be out of date.
            <button class="btn-sm" @click=${this._reloadStaleRecord}>Reload</button>
          </p>`
        : ''}
      ${this._mode === 'list' ? this._renderList() : this._renderDetail()}
    `;
  }

  private _renderList() {
    return html`
      <div class="tab-body">
        <div class="list-tools">
          <input placeholder="Search members by name, code or email…" .value=${this._search}
            @input=${(e: InputEvent) => this._search = (e.target as HTMLInputElement).value}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._loadList()} />
          <button class="btn" @click=${this._loadList}>Search</button>
          ${this.isAdmin ? html`<button class="btn btn-sec" @click=${this._startCreate}>＋ New Member</button>` : ''}
        </div>
        ${this._loading ? html`<div class="loading">Loading…</div>` : ''}
        ${!this._loading && this._items.length === 0 ? html`<div class="empty">No members found.</div>` : ''}
        ${this._items.length > 0 ? html`
          <div class="table-wrap">
            <table>
              <thead>
                <tr><th>Code</th><th>Name</th><th>Email</th><th>Tel</th><th class="num">Points</th><th></th></tr>
              </thead>
              <tbody>
                ${this._items.map((r) => html`
                  <tr class="clickable" @click=${() => this._openDetail(String(r['id']))}>
                    <td>${r['code'] || '—'}</td>
                    <td>${r['name'] || '—'}</td>
                    <td>${r['email'] || '—'}</td>
                    <td>${r['telNo'] || '—'}</td>
                    <td class="num">${r['pointBalance'] ?? 0}</td>
                    <td>
                      <button class="btn btn-sm"
                        @click=${(e: Event) => { e.stopPropagation(); this._openDetail(String(r['id'])); }}>
                        ${this.isAdmin ? '✏️ Edit' : '👁 View'}
                      </button>
                    </td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        ` : ''}
      </div>
    `;
  }

  private _renderDetail() {
    const d = this._draft;
    if (!d) return html`<div class="loading">Loading…</div>`;
    const tabs = this._mode === 'create' ? TABS.filter((t) => t.id === 'info') : TABS;

    return html`
      <div class="detail-head">
        <button class="btn btn-sec btn-sm" @click=${this._backToList}>← Back</button>
        <h3>${this._mode === 'create' ? 'New Member' : `${this._detail?.code || ''} — ${this._detail?.name || ''}`}</h3>
        ${this._mode === 'detail' ? html`<span class="badge">${this._detail?.pointBalance ?? 0} pts</span>` : ''}
        <div class="spacer"></div>
        ${this.isAdmin && this._mode === 'detail' ? html`
          <button class="btn btn-danger btn-sm" @click=${this._delete}>Delete</button>
        ` : ''}
        ${this.isAdmin ? html`
          <button class="btn btn-sec btn-sm" ?disabled=${!this._dirty} @click=${() => { this._draft = { ...(this._detail || {}) } as Partial<MemberDetail>; this._dirty = false; }}>Discard</button>
          <button class="btn btn-sm" ?disabled=${this._saving || !this._dirty && this._mode === 'detail'} @click=${this._save}>
            ${this._saving ? 'Saving…' : this._mode === 'create' ? 'Create' : 'Save'}
          </button>
        ` : ''}
      </div>

      <div class="tabs">
        ${tabs.map((t) => html`
          <button class=${this._tab === t.id ? 'active' : ''} @click=${() => this._switchTab(t.id)}>${t.label}</button>
        `)}
      </div>

      <div class="tab-body">
        ${this._tab === 'info' ? this._renderInfo() : ''}
        ${this._tab === 'rewards' ? this._renderRewards() : ''}
        ${this._tab === 'history' ? this._renderHistory() : ''}
        ${this._tab === 'account' ? this._renderAccount() : ''}
      </div>
    `;
  }

  private _renderInfo() {
    const d = this._draft!;
    return html`
      <div class="grid">
        ${this._text('Code', 'code', d.code)}
        ${this._text('Name', 'name', d.name)}
        ${this._text('IC / ID No', 'ic', d.ic)}
        ${this._text('Email', 'email', d.email, 'email')}
        ${this._text('Tel No', 'telNo', d.telNo)}
        ${this._text('Fax No', 'faxNo', d.faxNo)}
        ${this._text('Contact Person', 'contact', d.contact)}
        ${this._text('Postcode', 'postcode', d.postcode)}
        ${this._text('City', 'city', d.city)}
        ${this._text('State', 'state', d.state)}
        ${this._text('Country', 'country', d.country)}
        ${this._text('ID Type', 'idType', d.idType)}
        ${this._text('TIN', 'tin', d.tin)}
        ${this._dobField()}
        ${this._text('Enroll No', 'enrollNumber', d.enrollNumber)}
        ${this._number('Discount %', 'discount', d.discount)}
        ${this._number('Credit Term', 'creditTerm', d.creditTerm)}
        ${this._number('Debit Term', 'debitTerm', d.debitTerm)}
        ${this._number('Credit Limit', 'creditLimit', d.creditLimit)}
        ${this._text('Purpose of Sales', 'purposeOfSales', d.purposeOfSales)}
        ${this._bool('Taxable', 'isTaxable', d.isTaxable)}
        ${this._bool('Can Receive Poison', 'canSellPoison', d.canSellPoison)}
        ${this._area('Address', 'address', d.address, true)}
        ${this._area('Notes', 'notes', d.notes, true)}
        ${this._area('Allergies', 'allergy', d.allergy, true)}
        ${this._area('Medical History', 'medicalHistory', d.medicalHistory, true)}
        ${this._area('Long Term Medication', 'longTermMedication', d.longTermMedication, true)}
        ${this._mode === 'detail' ? html`
          <div class="fg"><label>Gender</label><div class="ro">${d.gender || '—'}</div></div>
          <div class="fg"><label>Blood Type</label><div class="ro">${d.bloodType || '—'}</div></div>
          <div class="fg"><label>Race</label><div class="ro">${d.race || '—'}</div></div>
          <div class="fg"><label>Marital Status</label><div class="ro">${d.maritalStatus || '—'}</div></div>
          <div class="fg"><label>Points Balance</label><div class="ro">${d.pointBalance ?? 0}</div></div>
        ` : ''}
      </div>
    `;
  }

  private _renderRewards() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._points.length === 0) return html`<div class="empty">No reward points recorded yet.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Outlet</th><th>Date</th><th>Receipt #</th><th class="num">Amount</th><th class="num">Points</th><th class="num">Balance</th></tr></thead>
          <tbody>
            ${this._points.map((r) => html`
              <tr>
                <td>${r.where || '—'}</td>
                <td>${this._fmtDate(r.date)}</td>
                <td>${r.receiptNo || '—'}</td>
                <td class="num">${this._fmtMoney(r.amount)}</td>
                <td class="num rating">${r.points ?? 0}</td>
                <td class="num">${r.balance ?? 0}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderHistory() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._history.length === 0) return html`<div class="empty">No purchase history.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Ref</th><th>Code</th><th>Item</th><th class="num">Qty</th><th class="num">Price</th><th class="num">Amount</th><th>Outlet</th></tr></thead>
          <tbody>
            ${this._history.map((r) => html`
              <tr>
                <td>${this._fmtDate(r.date)}</td>
                <td>${r.refNo || '—'}</td>
                <td>${r.itemCode || '—'}</td>
                <td>${r.item || '—'}</td>
                <td class="num">${r.qty ?? '—'}</td>
                <td class="num">${this._fmtMoney(r.price)}</td>
                <td class="num">${this._fmtMoney(r.amount)}</td>
                <td>${r.location || '—'}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private _renderAccount() {
    if (this._tabLoading) return html`<div class="loading">Loading…</div>`;
    if (this._tabError) return html`<p class="err">${this._tabError}</p>`;
    if (this._account.length === 0) return html`<div class="empty">No account transactions.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Ref</th><th class="num">Debit</th><th class="num">Credit</th><th>Remark</th></tr></thead>
          <tbody>
            ${this._account.map((r) => html`
              <tr>
                <td>${this._fmtDate(r.date)}</td>
                <td>${r.refNo || '—'}</td>
                <td class="num debit">${r.debit ? this._fmtMoney(r.debit) : '—'}</td>
                <td class="num">${r.credit ? this._fmtMoney(r.credit) : '—'}</td>
                <td>${r.remark || '—'}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }
}
